/**
 * AreasTab Component
 * Roof area measurements, upstands, and detail quantities
 */

import React from 'react';
import { Card, Button, EditableCell } from '../ui/FormElements';
import { generateId, calculateGirth } from '../../utils/calculations';
import { EDGE_DETAIL_TYPES } from '../../constants/flatCalc';

const AreasTab = ({ data, onChange, spec, onSpecChange, onNext }) => {
  const { areas, upstands, details } = data;

  // Area handlers
  const addArea = () => {
    onChange({
      ...data,
      areas: [...areas, { id: generateId(), name: '', length: 0, width: 0, deductions: 0 }]
    });
  };

  const updateArea = (id, field, value) => {
    onChange({
      ...data,
      areas: areas.map(a => a.id === id ? { ...a, [field]: value } : a)
    });
  };

  const removeArea = (id) => {
    onChange({ ...data, areas: areas.filter(a => a.id !== id) });
  };

  // Upstand handlers
  const addUpstand = () => {
    onChange({
      ...data,
      upstands: [...upstands, {
        id: generateId(),
        type: 'edge_trim',
        location: '',
        length: 0,
        height: 300,
        width: 300
      }]
    });
  };

  const updateUpstand = (id, field, value) => {
    onChange({
      ...data,
      upstands: upstands.map(u => u.id === id ? { ...u, [field]: value } : u)
    });
  };

  const removeUpstand = (id) => {
    onChange({ ...data, upstands: upstands.filter(u => u.id !== id) });
  };

  // Details handler
  const updateDetails = (field, value) => {
    onChange({ ...data, details: { ...details, [field]: value } });
  };

  // Calculations
  const totalArea = areas.reduce((sum, a) => {
    return sum + (a.length || 0) * (a.width || 0) - (a.deductions || 0);
  }, 0);

  const totalUpstandLength = upstands.reduce((sum, u) => sum + (u.length || 0), 0);
  
  const showBondingArea = spec.insulationFixingMethod === 'adhered';

  return (
    <div>
      {/* Roof Areas */}
      <Card
        title="Roof Areas"
        action={<Button size="sm" onClick={addArea}>+ Add Area</Button>}
      >
        {areas.length === 0 ? (
          <p className="text-slate-500 text-center py-4">No areas added.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-right py-2 px-2 w-28">Length (m)</th>
                  <th className="text-right py-2 px-2 w-28">Width (m)</th>
                  <th className="text-right py-2 px-2 w-28">Deductions</th>
                  <th className="text-right py-2 px-2 w-28">Net Area</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {areas.map(area => (
                  <tr key={area.id} className="border-b border-slate-700/50">
                    <td className="py-1 px-2">
                      <input
                        type="text"
                        value={area.name}
                        placeholder="e.g. Main Roof"
                        onChange={(e) => updateArea(area.id, 'name', e.target.value)}
                        className="w-full bg-transparent text-white focus:outline-none"
                      />
                    </td>
                    <td className="py-1 px-2">
                      <EditableCell
                        value={area.length}
                        onChange={(v) => updateArea(area.id, 'length', v)}
                      />
                    </td>
                    <td className="py-1 px-2">
                      <EditableCell
                        value={area.width}
                        onChange={(v) => updateArea(area.id, 'width', v)}
                      />
                    </td>
                    <td className="py-1 px-2">
                      <EditableCell
                        value={area.deductions}
                        onChange={(v) => updateArea(area.id, 'deductions', v)}
                      />
                    </td>
                    <td className="py-1 px-2 text-right text-white">
                      {((area.length || 0) * (area.width || 0) - (area.deductions || 0)).toFixed(2)}
                    </td>
                    <td className="py-1 px-2">
                      <button
                        onClick={() => removeArea(area.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-700/30 font-semibold">
                  <td colSpan="4" className="py-2 px-2 text-white">Total Area</td>
                  <td className="py-2 px-2 text-right text-amber-500">{totalArea.toFixed(2)} m²</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Insulation Bonding Area */}
      {showBondingArea && (
        <Card title="Insulation Bonding">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Bonding Area (m²)</label>
              <input
                type="number"
                value={spec.insulationBondingArea || ''}
                onChange={(e) => onSpecChange({
                  ...spec,
                  insulationBondingArea: parseFloat(e.target.value) || 0
                })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                placeholder="Enter bonding area"
              />
              <p className="text-slate-500 text-xs mt-1">
                Total bonding area × {spec.insulationLayers || 1} layer(s) = {' '}
                {((spec.insulationBondingArea || 0) * (spec.insulationLayers || 1)).toFixed(2)} m²
              </p>
            </div>
            <div className="flex items-center">
              <p className="text-slate-400 text-sm">
                Total roof area: <span className="text-white font-medium">{totalArea.toFixed(2)} m²</span>
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Upstands & Perimeter */}
      <Card
        title="Upstands & Perimeter"
        action={<Button size="sm" onClick={addUpstand}>+ Add Upstand</Button>}
      >
        {upstands.length === 0 ? (
          <p className="text-slate-500 text-center py-4">No upstands added.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 px-2 w-40">Type</th>
                  <th className="text-left py-2 px-2">Location</th>
                  <th className="text-right py-2 px-2 w-24">Length</th>
                  <th className="text-right py-2 px-2 w-24">Height</th>
                  <th className="text-right py-2 px-2 w-24">Width</th>
                  <th className="text-right py-2 px-2 w-24">Girth</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {upstands.map(u => {
                  const detailType = EDGE_DETAIL_TYPES.find(t => t.id === u.type);
                  const girth = calculateGirth(u.type, u.height, u.width);
                  
                  return (
                    <tr key={u.id} className="border-b border-slate-700/50">
                      <td className="py-1 px-2">
                        <select
                          value={u.type}
                          onChange={(e) => updateUpstand(u.id, 'type', e.target.value)}
                          className="w-full bg-slate-700 border-0 text-white text-sm rounded px-2 py-1"
                        >
                          {EDGE_DETAIL_TYPES.map(t => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1 px-2">
                        <input
                          type="text"
                          value={u.location}
                          placeholder="e.g. North"
                          onChange={(e) => updateUpstand(u.id, 'location', e.target.value)}
                          className="w-full bg-transparent text-white focus:outline-none"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <EditableCell
                          value={u.length}
                          onChange={(v) => updateUpstand(u.id, 'length', v)}
                        />
                      </td>
                      <td className="py-1 px-2">
                        {detailType?.hasHeight ? (
                          <EditableCell
                            value={u.height}
                            onChange={(v) => updateUpstand(u.id, 'height', v)}
                          />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {detailType?.hasWidth ? (
                          <EditableCell
                            value={u.width}
                            onChange={(v) => updateUpstand(u.id, 'width', v)}
                          />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="py-1 px-2 text-right text-white">
                        {girth > 0 ? girth : '—'}
                      </td>
                      <td className="py-1 px-2">
                        <button
                          onClick={() => removeUpstand(u.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-700/30 font-semibold">
                  <td colSpan="2" className="py-2 px-2 text-white">Total Length</td>
                  <td className="py-2 px-2 text-right text-amber-500">{totalUpstandLength.toFixed(2)} m</td>
                  <td colSpan="4"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Details */}
      <Card title="Details">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            ['outlets', 'Outlets'],
            ['penetrationsSmall', 'Penetrations (Small)'],
            ['penetrationsLarge', 'Penetrations (Large)'],
            ['rooflights', 'Rooflights'],
            ['internalAngles', 'Internal Angles'],
            ['externalAngles', 'External Angles']
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-slate-400 text-sm mb-2">{label}</label>
              <input
                type="number"
                value={details[key] || 0}
                onChange={(e) => updateDetails(key, parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
              />
            </div>
          ))}
        </div>
      </Card>
      
      {/* Navigation */}
      <div className="flex justify-end mt-6">
        <Button onClick={onNext}>Next: Materials →</Button>
      </div>
    </div>
  );
};

export default AreasTab;
